import { useState, useEffect } from "react";
import { useTemplateStore } from "@/zustand/templateStore.js";
import { useProductData } from "@/zustand/productStore.js";
import { useCartData } from "@/zustand/cartStore.js";
import { useMarketingStore } from "@/zustand/emailMarketingStore.js";
import { ChevronDownIcon, DoubleTagIcon, QuestionMarkIcon } from "@/utils/miscIcons.jsx";
import { useOrderCalculations } from "@/func/orderCalculations.js";
import { formatPrice } from "@/func/priceNotation";
import ProductsList from "@/components/productsList";
import DiscountInput from "@/components/inputs/DiscountInput";
import ExtendedWarrantyCheckbox from "@/components/orderSummary/ExtendedWarrantyCheckbox";
import { isDiscountActive, isDiscountEnabledForSelection } from "@/utils/discounts";

// The shipping policy link may point at an external mid-domain (domain
// replacer); open it in a new tab while keeping same-host links in place.
const opensInNewTab = (url) => {
  try {
    return new URL(url, window.location.href).host !== window.location.host;
  } catch {
    return false;
  }
};

// Order summary for a checkout without the product picker. Always expanded in
// the desktop summary column; on mobile (`collapsible`) it sits under the
// header as a closed "Order summary" bar showing the total.
const OrderOverviewDrawerV4 = ({ collapsible = false }) => {
  const productData = useProductData();
  const { isMobile } = useTemplateStore();
  const [open, setOpen] = useState(false);
  const [warrantyEnabled, setWarrantyEnabled] = useState(null);
  const [warrantyTooltipVisible, setWarrantyTooltipVisible] = useState(false);
  const {
    selectedProduct,
    warrantyState,
    setWarrantyState,
    isDiscountApplied,
    setCartTotal,
    isSubscribe,
    discountCode,
    premiumShippingUpsellEnabled,
  } = useCartData();
  const { freeShippingApplied } = useMarketingStore();
  const summary = window.post_template?.checkout_v4_summary;
  const warrantySection = window.post_template?.checkout_v4_warranty;
  const hideWarrantyCheckbox = !!warrantySection?.checkout_v4_warranty_hide_checkbox;
  const warrantySummaryLabel = warrantySection?.tr_checkout_v4_warranty_summary_label || "Extended warranty";
  const showWarrantyExplanationIcon = !!warrantySection?.checkout_v4_warranty_show_explanation_icon;
  const warrantyExplanationText = warrantySection?.tr_checkout_v4_warranty_explanation_text;
  const premiumShippingLabel =
    window.post_template?.checkout_v4_premium_shipping?.tr_checkout_v4_premium_shipping_summary_label ||
    "Premium Shipping";
  const shippingPolicyUrl = summary?.checkout_v4_summary_shippingLink;
  const hideTotalSavings = summary?.checkout_v4_summary_hide_total_savings;
  // Order Summary colour fields; empty fields keep the design colours from the stylesheet.
  const colorVars = Object.fromEntries(
    Object.entries({
      "--cc-v4-summary-bg": summary?.checkout_v4_summary_background,
      "--cc-v4-summary-text": summary?.checkout_v4_summary_color,
      "--cc-v4-summary-savings-text": summary?.checkout_v4_summary_savingsTextColor,
    }).filter(([, value]) => value)
  );

  const discountEnabledForSelection = isDiscountEnabledForSelection(productData, isSubscribe);
  const discountActive = isDiscountActive(productData, isSubscribe, discountCode, isDiscountApplied);

  const orderCalculations = useOrderCalculations(
    selectedProduct,
    discountActive,
    productData?.multiply_price_by_amount,
    warrantyState,
    productData.formatted_string
  );

  useEffect(() => {
    setCartTotal(orderCalculations?.total_numeric);
  }, [orderCalculations?.total]);

  useEffect(() => {
    setWarrantyEnabled(productData?.add_warranty_for_product);
  }, [productData?.add_warranty_for_product]);

  useEffect(() => {
    // the checkbox is hidden, so the customer cannot opt out — warranty is always applied
    if (warrantyEnabled && hideWarrantyCheckbox) {
      setWarrantyState(true);
    }
  }, [warrantyEnabled, hideWarrantyCheckbox]);

  const savingsNumeric = Number(orderCalculations?.savings_numeric);
  const totalBeforeSavings =
    savingsNumeric > 0 && productData?.formatted_string
      ? formatPrice((Number(orderCalculations.total_numeric) + savingsNumeric).toFixed(2), productData.formatted_string)
      : null;

  // Same quantities as the product card badges: the pack amount plus any bonus products.
  const itemsCount =
    (Number(selectedProduct?.amount) || 0) +
    (selectedProduct?.enable_fake_products && Array.isArray(selectedProduct?.fake_products)
      ? selectedProduct.fake_products.reduce((sum, product) => sum + (Number(product?.amount) || 0), 0)
      : 0);
  const itemsLabel =
    itemsCount === 1
      ? summary?.tr_checkout_v4_summary_itemLabel || "item"
      : summary?.tr_checkout_v4_summary_itemsLabel || "items";

  const expanded = !collapsible || open;

  return (
    <div
      className={`orderOverviewDrawerV4 ${collapsible ? "orderOverviewDrawerV4--collapsible" : ""}`}
      style={colorVars}
    >
      {collapsible && (
        <button
          type="button"
          data-cy="cc-order-review-opener"
          className={`orderOverviewDrawerV4__toggle ${open ? "_open" : ""}`}
          aria-expanded={open}
          onClick={() => setOpen(!open)}
        >
          <span className="orderOverviewDrawerV4__toggle__title">
            {summary?.tr_checkout_v4_order_review_accordion_title}
            <ChevronDownIcon />
          </span>
          <span className="orderOverviewDrawerV4__toggle__prices">
            {totalBeforeSavings && (
              <span className="orderOverviewDrawerV4__toggle__oldTotal">{totalBeforeSavings}</span>
            )}
            <span className="orderOverviewDrawerV4__toggle__total">{orderCalculations?.total}</span>
          </span>
        </button>
      )}

      <div
        data-cy="cc-order-summary-container"
        className={`orderOverviewDrawerV4__content ${expanded ? "" : "orderOverviewDrawerV4__content--hidden"}`}
      >
        <ProductsList />
        <div className="orderOverviewDrawerV4__divider" />

        {discountEnabledForSelection && (
          <>
            <div className="orderOverviewDrawerV4__discount">
              <DiscountInput />
            </div>
            <div className="orderOverviewDrawerV4__divider" />
          </>
        )}

        {warrantyEnabled && !hideWarrantyCheckbox && (
          <>
            <div className="orderOverviewDrawerV4__warranty">
              <ExtendedWarrantyCheckbox />
            </div>
            <div className="orderOverviewDrawerV4__divider" />
          </>
        )}

        <div className="orderTotals">
          <div className="orderTotals__item">
            <p>
              {summary?.tr_checkout_v4_summary_orderLabel}
              {itemsCount > 0 && <span data-testid="summary-items-count">{` • ${itemsCount} ${itemsLabel}`}</span>}
            </p>
            <p data-cy="summary-subtotal">{orderCalculations?.subtotal}</p>
          </div>
          <div className="orderTotals__item">
            <p data-testid="summary-shipping-label" className="orderTotals__item__shippingLabel">
              <span>
                {premiumShippingUpsellEnabled ? premiumShippingLabel : summary?.tr_checkout_v4_summary_shippingLabel}
              </span>
              {shippingPolicyUrl && (
                <a
                  className="orderTotals__item__shippingLabel--link"
                  href={shippingPolicyUrl}
                  {...(opensInNewTab(shippingPolicyUrl) ? { target: "_blank", rel: "noopener noreferrer" } : {})}
                >
                  <QuestionMarkIcon />
                </a>
              )}
            </p>
            <p data-cy="summary-shipping">{orderCalculations?.shipping}</p>
          </div>
          {warrantyEnabled && hideWarrantyCheckbox && (
            <div className="orderTotals__item">
              <p data-testid="summary-warranty-label" className="orderTotals__item__shippingLabel">
                <span>{warrantySummaryLabel}</span>
                {showWarrantyExplanationIcon && warrantyExplanationText && (
                  <span
                    className="orderTotals__item__warrantyHelper"
                    onClick={() => {
                      if (isMobile) {
                        setWarrantyTooltipVisible(!warrantyTooltipVisible);
                      }
                    }}
                  >
                    <QuestionMarkIcon />
                    <span className="orderTotals__item__warrantyComment" visible={warrantyTooltipVisible.toString()}>
                      {warrantyExplanationText}
                    </span>
                  </span>
                )}
              </p>
              <p data-cy="summary-warranty">{orderCalculations?.warranty}</p>
            </div>
          )}
          {freeShippingApplied && (
            <div className="orderOverviewDrawerV3__freeShippingLabelBottom">
              {summary?.tr_checkout_v4_summary_free_shipping_applied_label}
            </div>
          )}
        </div>
        <div className="orderOverviewDrawerV4__divider" />

        <div className="orderOverviewDrawerV4__totals">
          <div className="total">
            <div>{summary?.tr_checkout_v4_summary_totalLabel}</div>
            <div data-cy="summary-total">{orderCalculations?.total}</div>
          </div>
          {!hideTotalSavings && savingsNumeric > 0 && (
            <div className="orderOverviewDrawerV4__savings">
              <span className="orderOverviewDrawerV4__savings__label">
                <DoubleTagIcon />
                <span data-testid="total-savings-label">{summary?.tr_checkout_v4_summary_savingsLabel}</span>
              </span>
              <span data-testid="total-savings" className="orderOverviewDrawerV4__savings__amount">
                -{orderCalculations?.savings}
              </span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default OrderOverviewDrawerV4;
